import React, { useEffect, useRef } from "react";

const Modal = ({
  isOpen,
  message,
  showDownload = true,
  onRetry,
  onClose,
  isError,
  disneyQuote,
  children,
}) => {
  const modalRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Focus the modal container
      modalRef.current?.focus();
      // Trap focus inside modal
      const handleTab = (e) => {
        const focusableEls = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];
        if (e.key === "Tab") {
          if (e.shiftKey) {
            if (document.activeElement === firstEl) {
              e.preventDefault();
              lastEl.focus();
            }
          } else {
            if (document.activeElement === lastEl) {
              e.preventDefault();
              firstEl.focus();
            }
          }
        }
        if (e.key === "Escape") {
          onClose?.();
        }
      };
      document.addEventListener("keydown", handleTab);
      return () => document.removeEventListener("keydown", handleTab);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isLoading =
    !isError &&
    typeof message === "string" &&
    message.toLowerCase().includes("submitting");
  
  const isSuccess = 
    !isError && 
    typeof message === "string" && 
    message.toLowerCase() === "success";

  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div
        ref={modalRef}
        className="p-6 w-full max-w-md text-center bg-white rounded-xl shadow-lg outline-none"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        tabIndex={-1}
      >
        {typeof message === 'string' && !isSuccess ? (
          <h2
            id="modal-title"
            className={`text-lg font-semibold mb-4 ${
              isError ? "text-red-600" : "text-primary"
            }`}
          >
            {message}
          </h2>
        ) : null}
        
        {isSuccess && (
          <h2
            id="modal-title"
            className="text-lg font-semibold mb-4 text-green-600"
          >
            Success! Your trip information has been submitted.
          </h2>
        )}
        
        <div id="modal-desc">
          {/* Custom content passed as children */}
          {children}
          
          {/* Spinner for loading state */}
          {isLoading && (
            <div className="flex flex-col items-center mt-4 space-y-4">
              <div className="w-8 h-8 rounded-full border-4 animate-spin border-primary border-t-transparent" />
              {disneyQuote && (
                <p className="max-w-xs text-sm italic text-center text-textSecondary">
                  {disneyQuote}
                </p>
              )}
            </div>
          )}

          {/* Spinner for success state (downloadable) */}
          {isSuccess && showDownload && !children && (
            <div className="flex justify-center mt-4">
              <div className="w-8 h-8 rounded-full border-4 animate-spin border-green-500 border-t-transparent" />
            </div>
          )}
        </div>

        {/* Retry + Close buttons for error */}
        {isError && (
          <div className="flex gap-4 justify-center mt-6">
            <button
              onClick={onRetry}
              className="px-4 py-2 text-sm text-white rounded-md bg-primary hover:bg-opacity-90"
            >
              Try Again
            </button>
            <button
              ref={closeBtnRef}
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-md border border-surface text-textPrimary hover:bg-gray-100"
              aria-label="Close modal"
            >
              Close
            </button>
          </div>
        )}
        
        {/* Close button for success state */}
        {isSuccess && (
          <div className="flex justify-center mt-6">
            <button
              ref={closeBtnRef}
              onClick={onClose}
              className="px-4 py-2 text-sm text-white rounded-md bg-green-600 hover:bg-green-700"
              aria-label="Close modal"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;